import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { submitLead, errorMessage } from "../../lib/api";
import { Field, SelectField, inputCls } from "./Field";
import { LocationPicker } from "./LocationPicker";
import { SubmitButton } from "./EmployerForm";
import { PhoneVerify } from "./PhoneVerify";
import { EMPTY_KYC, KycSection, kycMissing, kycPayload } from "./KycSection";
import { RolePicker } from "./RolePicker";
import { SkillChips } from "./SkillChips";
import { AcceptTerms } from "./AcceptTerms";
import { GENDER, MARITAL, RELOCATION, SKILLS } from "./data";
import { SuccessCard } from "./SuccessCard";

const EMPTY = {
  full_name: "", father_name: "", mother_name: "", mobile: "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "", height_cm: "", weight_kg: "", gender: "", marital_status: "", state: "", district: "",
  relocation: "", skill_category: "", department: "", job_role: "", skills: [], expected_salary: "", full_address: "", has_esic: false, esic_number: "", uan_number: "", phone_verification_token: "", kyc: EMPTY_KYC, accepted_terms: false,
};

const REQUIRED = { gender: "Male / Female", marital_status: "Married / Unmarried", skill_category: "Skill category", department: "Department", job_role: "Job role", state: "State", district: "District", relocation: "Relocation preference" };

const num = (v) => (v === "" ? null : Number(v));

const ageOf = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const t = new Date();
  return t.getFullYear() - d.getFullYear() - (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate()) ? 1 : 0);
};

const maxDob = () => {
  const t = new Date();
  t.setFullYear(t.getFullYear() - 18);
  return t.toISOString().slice(0, 10);
};

export const WorkerForm = ({ prefill, active, onContext }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (active) onContext({ tab: "worker", job_role: f.job_role, state: f.state, district: f.district });
  }, [active, onContext, f.job_role, f.state, f.district]);
  useEffect(() => {
    if (!prefill) return;
    setDone(null);
    setF((p) => ({ ...p, department: prefill.department, job_role: prefill.job_role, skills: p.department === prefill.department ? p.skills : [] }));
  }, [prefill]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const isSecurity = f.skill_category === "security";

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = Object.keys(REQUIRED).find((k) => !f[k]);
    if (missing) return toast.error(`Please select ${REQUIRED[missing]}`);
    if (!f.skills.length) return toast.error("Please select at least one skill");
    if (f.has_esic && f.esic_number.length !== 10) return toast.error("ESIC number must be 10 digits");
    const kycErr = kycMissing(f.kyc);
    if (kycErr) return toast.error(kycErr);
    if (!f.accepted_terms) return toast.error("Please accept the Legal Disclaimer, Terms and Privacy Policy");
    setBusy(true);
    try {
      const { has_esic, ...rest } = f;
      const res = await submitLead("worker", {
        ...rest, age: null, expected_salary: num(f.expected_salary), height_cm: num(f.height_cm), weight_kg: num(f.weight_kg), uan_number: f.uan_number || null, esic_number: (has_esic && f.esic_number) || null, mother_name: f.mother_name.trim() || null,
        phone_verification_token: f.phone_verification_token || null, kyc: kycPayload(f.kyc),
      });
      setDone(res);
      setF(EMPTY);
      toast.success("Registration received. We will contact you when a matching job opens.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <SuccessCard kind="worker" reference={done.reference} onReset={() => setDone(null)} />;

  return (
    <form onSubmit={onSubmit} data-testid="worker-form" className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Full Name">
        <Input required data-testid="worker-input-name" className={inputCls} value={f.full_name}
          onChange={(e) => set("full_name")(e.target.value)} placeholder="As on ID proof" />
      </Field>
      <Field label="Father's Name">
        <Input required data-testid="worker-input-father-name" className={inputCls} value={f.father_name}
          onChange={(e) => set("father_name")(e.target.value)} placeholder="Father's full name" />
      </Field>
      <Field label="Mother's Name" hint="optional">
        <Input data-testid="worker-input-mother-name" className={inputCls} value={f.mother_name}
          onChange={(e) => set("mother_name")(e.target.value)} placeholder="Mother's full name" />
      </Field>
      <PhoneVerify prefix="worker" label="Mobile Number" phone={f.mobile} token={f.phone_verification_token}
        onPhone={(v) => setF((p) => ({ ...p, mobile: v, phone_verification_token: "" }))} onToken={set("phone_verification_token")} />
      <SelectField label="Skill Category" value={f.skill_category} onChange={set("skill_category")} options={SKILLS} testId="worker-select-skill-category" />
      <RolePicker prefix="worker" roleLabel="Preferred Job Role" department={f.department} jobRole={f.job_role}
        onChange={(d, r) => setF((p) => ({ ...p, department: d, job_role: r, skills: d === p.department ? p.skills : [] }))} />
      <SkillChips prefix="worker" department={f.department} value={f.skills} onChange={set("skills")} hint="select all that apply" />
      <Field label="Expected Monthly Salary (₹)">
        <Input required type="number" min={1000} max={500000} data-testid="worker-input-expected-salary" className={inputCls}
          value={f.expected_salary} onChange={(e) => set("expected_salary")(e.target.value)} placeholder="e.g. 15000" />
      </Field>
      <SelectField label="Gender" value={f.gender} onChange={set("gender")} options={GENDER} testId="worker-select-gender" />
      <SelectField label="Marital Status" value={f.marital_status} onChange={set("marital_status")} options={MARITAL} testId="worker-select-marital" />
      <Field label="Date of Birth" hint={ageOf(f.date_of_birth) !== null ? `Age: ${ageOf(f.date_of_birth)} years` : "as on ID proof"}>
        <Input required type="date" max={maxDob()} data-testid="worker-input-dob" className={`${inputCls} [color-scheme:dark]`}
          value={f.date_of_birth} onChange={(e) => set("date_of_birth")(e.target.value)} />
      </Field>
      <Field label="Height (cm)" hint={isSecurity ? "required for security" : "optional"}>
        <Input required={isSecurity} type="number" min={100} max={230} data-testid="worker-input-height" className={inputCls}
          value={f.height_cm} onChange={(e) => set("height_cm")(e.target.value)} placeholder="e.g. 172" />
      </Field>
      <Field label="Weight (kg)" hint={isSecurity ? "required for security" : "optional"}>
        <Input required={isSecurity} type="number" min={30} max={200} data-testid="worker-input-weight" className={inputCls}
          value={f.weight_kg} onChange={(e) => set("weight_kg")(e.target.value)} placeholder="e.g. 70" />
      </Field>
      <LocationPicker prefix="worker" stateLabel="Current State / UT" state={f.state} district={f.district}
        onChange={(s, d) => setF((p) => ({ ...p, state: s, district: d }))} />
      <SelectField label="Willing to Relocate" value={f.relocation} onChange={set("relocation")} options={RELOCATION} testId="worker-select-relocation" />
      <Field label="UAN Number" hint="optional, existing PF account">
        <Input inputMode="numeric" maxLength={12} data-testid="worker-input-uan" className={inputCls} value={f.uan_number}
          onChange={(e) => set("uan_number")(e.target.value.replace(/\D/g, ""))} placeholder="12-digit UAN" />
      </Field>
      <div className="space-y-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-300">
          <Checkbox data-testid="worker-checkbox-esic" checked={f.has_esic}
            onCheckedChange={(v) => setF((p) => ({ ...p, has_esic: v === true, esic_number: v === true ? p.esic_number : "" }))}
            className="rounded-none border-slate-500 data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500" />
          I have an ESIC number
        </label>
        {f.has_esic && (
          <Input required inputMode="numeric" minLength={10} maxLength={10} data-testid="worker-input-esic" className={inputCls} value={f.esic_number}
            onChange={(e) => set("esic_number")(e.target.value.replace(/\D/g, ""))} placeholder="10-digit ESIC IP number" />
        )}
      </div>
      <div className="md:col-span-2">
        <Field label="Full Address" hint="house no, street, village/city, PIN">
          <Textarea required minLength={10} maxLength={300} data-testid="worker-input-address" className={`${inputCls} h-20`} value={f.full_address}
            onChange={(e) => set("full_address")(e.target.value)} placeholder="e.g. 12, Gandhi Nagar, Balangir, Odisha - 767001" />
        </Field>
      </div>
      <Field label="Emergency Contact Name">
        <Input required data-testid="worker-input-emergency-name" className={inputCls} value={f.emergency_contact_name}
          onChange={(e) => set("emergency_contact_name")(e.target.value)} placeholder="e.g. father, wife, brother" />
      </Field>
      <Field label="Emergency Contact Number">
        <div className="flex">
          <span className="flex h-11 items-center border border-r-0 border-white/10 bg-[#1E293B] px-3 font-mono text-sm text-slate-400">+91</span>
          <Input required inputMode="numeric" data-testid="worker-input-emergency-phone" className={inputCls} value={f.emergency_contact_phone}
            onChange={(e) => set("emergency_contact_phone")(e.target.value.replace(/\D/g, "").slice(-10))} placeholder="10-digit mobile" />
        </div>
      </Field>
      <p className="text-sm leading-relaxed text-slate-500 md:col-span-2">
        Ex-Serviceman or Gun Licence certificates, if applicable, are collected by our team after registration.
      </p>
      <KycSection prefix="worker" kyc={f.kyc} setKyc={(fn) => setF((p) => ({ ...p, kyc: fn(p.kyc) }))} />
      <AcceptTerms prefix="worker" checked={f.accepted_terms} onChange={set("accepted_terms")} />
      <SubmitButton busy={busy} testId="worker-submit-button" label="Register for Jobs" />
    </form>
  );
};
